import { memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { type AssetCustomFieldValue } from '@air/api/types/customFields';
import { ChevronDownIcon, DownloadIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { cn } from '@air/utils-tailwind';

import { DEFAULT_AMOUNT_OF_CUSTOM_FIELDS_TO_SHOW } from '~/components/AssetModalV2/constants';
import { useCollapsibleSectionsContext } from '~/components/AssetModalV2/InfoPanel/CollapsibleSectionsContextProvider';
import { CollapsibleSection } from '~/components/AssetModalV2/InfoPanel/components/CollapsibleSection';
import { useCurrentClipPermissions } from '~/components/PrivateAssetModal/hooks/useCurrentClipPermissions';
import { AssetCustomFieldsPanelContent } from '~/components/RightPanel/CustomFields/AssetCustomFieldsPanelContent';
import { PrivateAssetPanelCustomFieldDateInput } from '~/components/RightPanel/CustomFields/PrivateAssetPanelCustomFieldDateInput';
import { PrivateAssetPanelCustomFieldPlainText } from '~/components/RightPanel/CustomFields/PrivateAssetPanelCustomFieldPlainText';
import { PrivateAssetPanelCustomFieldSelect } from '~/components/RightPanel/CustomFields/PrivateAssetPanelCustomFieldSelect';
import { useGetAvailableCustomFields } from '~/hooks/useGetAvailableCustomFields';
import { usePrivateAssetCustomFieldsData } from '~/hooks/usePrivateAssetCustomFieldsData';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { centralizedBoardLibraryIdSelector } from '~/store/centralizedBoard/selectors';
import { centralizedClipAssetIdSelector, centralizedDisplayTitleSelector } from '~/store/centralizedClip/selectors';
import { downloadCustomFieldsCsv } from '~/utils/CustomFields/downloadCustomFieldsCsv';
import { sanitizeRestrictedPathChars } from '~/utils/FileUtils';
import { canSeeAssetCustomFields } from '~/utils/permissions/assetPermissions';

export const CUSTOM_FIELDS_SECTION = 'CUSTOM_FIELDS_SECTION';

const DOWNLOAD_CUSTOM_FIELDS_CSV_LABEL = 'Download custom fields as CSV';

export const PrivateCustomFieldsCollapsibleSection = memo(() => {
  const { customFieldsCsvDownload } = useTemporaryFeatureFlags();
  const { expandedSections, toggleSection } = useCollapsibleSectionsContext();
  const isExpanded = expandedSections.customFields;
  const assetId = useSelector(centralizedClipAssetIdSelector);
  const assetTitle = useSelector(centralizedDisplayTitleSelector);
  const { clipPermissions } = useCurrentClipPermissions();
  const canViewCustomFields = canSeeAssetCustomFields(clipPermissions);
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: customFields } = usePrivateAssetCustomFieldsData(
    { assetId, workspaceId: currentWorkspace?.id },
    { canViewCustomFields },
  );
  const urlLibraryId = useURLLibraryIdSelector();
  const libraryId = useSelector(centralizedBoardLibraryIdSelector);
  const { getAvailableCustomFields } = useGetAvailableCustomFields<AssetCustomFieldValue>({
    libraryId: libraryId || urlLibraryId,
  });
  const allVisibleCustomFields = useMemo(
    () => getAvailableCustomFields(customFields?.data),
    [customFields?.data, getAvailableCustomFields],
  );

  const handleDownloadCsv = useCallback(() => {
    if (allVisibleCustomFields?.length) {
      const safeName = sanitizeRestrictedPathChars(assetTitle);
      downloadCustomFieldsCsv(allVisibleCustomFields, `${safeName}-custom-fields.csv`);
    }
  }, [allVisibleCustomFields, assetTitle]);

  const [showRestOfFields, setShowRestOfFields] = useState(false);
  const firstThreeFields = customFields?.data.slice(0, DEFAULT_AMOUNT_OF_CUSTOM_FIELDS_TO_SHOW);
  const restOfFields = customFields?.data.slice(DEFAULT_AMOUNT_OF_CUSTOM_FIELDS_TO_SHOW);
  const firstThreeFieldsRender = useMemo(() => {
    return (
      <AssetCustomFieldsPanelContent
        fields={getAvailableCustomFields(firstThreeFields)}
        SelectCustomField={PrivateAssetPanelCustomFieldSelect}
        DateCustomField={PrivateAssetPanelCustomFieldDateInput}
        PlainTextCustomField={PrivateAssetPanelCustomFieldPlainText}
      />
    );
  }, [firstThreeFields, getAvailableCustomFields]);

  const restOfFieldsRender = useMemo(() => {
    return (
      <AssetCustomFieldsPanelContent
        fields={getAvailableCustomFields(restOfFields)}
        SelectCustomField={PrivateAssetPanelCustomFieldSelect}
        DateCustomField={PrivateAssetPanelCustomFieldDateInput}
        PlainTextCustomField={PrivateAssetPanelCustomFieldPlainText}
      />
    );
  }, [restOfFields, getAvailableCustomFields]);

  const handleOnClick = useCallback(() => {
    setShowRestOfFields((prev) => !prev);
  }, []);

  const onOpenChange = useCallback(() => {
    toggleSection('customFields');
  }, [toggleSection]);

  return (
    <CollapsibleSection
      title="Custom fields"
      isExpanded={isExpanded}
      onOpenChange={onOpenChange}
      data-testid={CUSTOM_FIELDS_SECTION}
    >
      <div data-testid="PRIVATE_CUSTOM_FIELDS_COLLAPSIBLE_SECTION_THREE_FIELDS">{firstThreeFieldsRender}</div>
      {showRestOfFields && !!restOfFields?.length && <div>{restOfFieldsRender}</div>}
      <div className="mt-2 flex">
        {!!restOfFields?.length && (
          <Button
            onClick={handleOnClick}
            size="small"
            appearance="outline"
            color="gray"
            data-testid="CUSTOM_FIELDS_TOGGLE_BUTTON"
            aria-expanded={showRestOfFields}
            suffix={
              <ChevronDownIcon size="sm" className={cn('transition-transform', showRestOfFields && 'rotate-180')} />
            }
          >
            {showRestOfFields ? 'Hide All Fields' : 'Show All Fields'}
          </Button>
        )}
        {customFieldsCsvDownload && (
          <Tooltip label={DOWNLOAD_CUSTOM_FIELDS_CSV_LABEL} side="left">
            <IconButton
              adsIcon
              icon={DownloadIcon}
              appearance="ghost"
              color="gray"
              size="small"
              label={DOWNLOAD_CUSTOM_FIELDS_CSV_LABEL}
              onClick={handleDownloadCsv}
              disabled={!allVisibleCustomFields?.length}
              className="ml-auto"
            />
          </Tooltip>
        )}
      </div>
    </CollapsibleSection>
  );
});

PrivateCustomFieldsCollapsibleSection.displayName = 'PrivateCustomFieldsCollapsibleSection';
